import { TextField } from "@cmsgov/ds-medicare-gov";
import React, { ChangeEventHandler, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { DateFields2 } from "../../common/components/DateFields2";
import LastNameSection from "../../common/components/LastNameSection";
import MedicareNumberSection from "../../common/components/MedicareNumberSection";
import { ForgotPassword1Fields, SmartErrorAlert } from "../../common/components/SmartErrorAlert";
import { InvalidField } from "../../common/functions/errorAlertContent/getAlertContent";
import { isValidMedicareNumber } from "../../common/functions/isValidMedicareNumber";
import { isValidYear } from "../../common/functions/isValidYear";
import translate from "../../common/functions/translate";
import { useConversion } from "../../common/hooks/use.analytics";
import { ForgotPasswordErrorsResponse, useForgotPasswordApi } from "../../common/hooks/useForgotPasswordApi";
import { DateObject } from "../../common/objects/models/date.field.value";
import { Buttons, FieldErrState, Step } from "./ForgotPassword";

interface EnterYourInfoProps {
    formStart: () => void
    setStep: React.Dispatch<React.SetStateAction<number>>
    forgotUsernameData?: {
        dateOfBirth: DateObject;
        lastName: string;
        medicareNumber: string;
        suffix: string;
        username: string;
    }
    setSecretQuestion: React.Dispatch<React.SetStateAction<string>>
    conversion: ReturnType<typeof useConversion>
    username: string
    setUsername: React.Dispatch<React.SetStateAction<string>>
    medicareNumber: string
    setMedicareNumber: React.Dispatch<React.SetStateAction<string>>
    lastName: string
    setLastName: React.Dispatch<React.SetStateAction<string>>
    suffix: string
    setSuffix: React.Dispatch<React.SetStateAction<string>>
    dateOfBirth: DateObject
    setDateOfBirth: React.Dispatch<React.SetStateAction<DateObject>>
    enterInformationAlert?: boolean
}

export const EnterYourInfo = (props: EnterYourInfoProps) => {
    const { 
        formStart, 
        setStep, 
        forgotUsernameData, 
        setSecretQuestion, 
        conversion, 
        username, 
        setUsername,
        medicareNumber,
        setMedicareNumber, 
        lastName,
        setLastName,
        suffix,
        setSuffix,
        dateOfBirth,
        setDateOfBirth,
        enterInformationAlert,
    } = props;

    const [submit1Attempted, setSubmit1Attempted] = useState(false);
    const [mbiTouched, setMbiTouched] = useState(false);
    const [usernameRef, setUsernameRef] = useState<HTMLElement | null>(null);
    const [usernameTouched, setUsernameTouched] = useState(false);
    const [usernameErrState, setUsernameErrState] = useState<FieldErrState>(null);
    const [lastNameRef, setLastNameRef] = useState<HTMLElement | null>(null);
    const [lastNameTouched, setLastNameTouched] = useState(false);
    const [lastNameErrState, setLastNameErrState] = useState<FieldErrState>(null);
    const [monthFieldRef, setMonthFieldRef] = useState<HTMLElement | null>(null);
    const [medicareNumRef, setMedicareNumRef] = useState<HTMLElement | null>(null);
    const [mbiErrState, setMbiErrState] = useState<FieldErrState>(null);
    const [dateOfBirthTouched, setDateOfBirthTouched] = useState(false);
    const [dateOfBirthErrState, setDateOfBirthErrState] = useState<FieldErrState>(null);
    const [enterInfoLoading, setEnterInfoLoading] = useState(false);
    const [step1InvalidFields, setStep1InvalidFields] = useState<InvalidField<ForgotPassword1Fields>[]>([]);
    const [step1ApiErrResponse, setStep1ApiErrResponse] = useState<ForgotPasswordErrorsResponse | null>(null);

    const infoHeader = translate('enter_your_information');
    const history = useHistory();
    const forgotPasswordApi = useForgotPasswordApi();

    useEffect(() => {
        if (forgotUsernameData) {
            setDateOfBirth(forgotUsernameData.dateOfBirth);
            setLastName(forgotUsernameData.lastName);
            setMedicareNumber(forgotUsernameData.medicareNumber);
            setSuffix(forgotUsernameData.suffix);
            setUsername(forgotUsernameData.username);
        }
    }, [forgotUsernameData, setDateOfBirth, setLastName, setSuffix, setUsername, setMedicareNumber]);

    useEffect(() => {
        const isValid = isValidMedicareNumber(medicareNumber);
        const isEmpty = !medicareNumber;
        if (isValid) {
            setMbiErrState(null);
        } else if (isEmpty) {
            setMbiErrState('empty');
        } else {
            setMbiErrState('invalid');
        }
    }, [medicareNumber]);

    useEffect(() => {
        setUsernameErrState(username.length ? null : 'empty');
    }, [username]);

    useEffect(() => {
        setLastNameErrState(lastName.length ? null : 'empty');
    }, [lastName]);

    useEffect(() => {
        const { month, day, year } = dateOfBirth;
        const isEmpty = !month && !day && !year;
        const isInvalid = !isValidYear(year);

        if (isEmpty) {
            setDateOfBirthErrState('empty');
        } else if (isInvalid) {
            setDateOfBirthErrState('invalid');
        } else {
            setDateOfBirthErrState(null);
        }
    }, [dateOfBirth]);

    const onStep1InvalidFields = (fields: InvalidField<ForgotPassword1Fields>[]) => {
        fields.forEach(f => {
            if (f.name === 'mbi') {
                setMbiErrState('invalid');
            }
            if (f.name === 'username') {
                setUsernameErrState('invalid');
            }
            if (f.name === 'lastName') {
                setLastNameErrState('invalid');
            }
            if (f.name === 'dateOfBirth') {
                setDateOfBirthErrState('invalid');
            }
        });
    }

    const getUsernameErrMessage = () => {
        if (!usernameTouched && !submit1Attempted) {
            return null;
        }
        return usernameErrState ? translate('enter_your_usermame_with_period') : null;
    };

    const onUsernameChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        formStart();
        const newName = event.target.value.trim();
        setUsername(newName);
    };

    const getMbiErrMessage = () => {
        if (!mbiTouched && !submit1Attempted) {
            return null;
        }
        if (mbiErrState === 'empty') {
            return translate('enter_your_medicare_number');
        }
        if (mbiErrState === 'invalid') {
            return translate('check_mbi_and_try_again')
        }
        return null;
    };

    const onMedicareNumberChange = (value: string) => {
        formStart();
        const newNum = value.trim();
        setMedicareNumber(newNum);
    };

    const getLastNameErrMessage = () => {
        if (!lastNameTouched && !submit1Attempted) {
            return null;
        }
        return lastNameErrState ? translate('enter_your_last_name') : null;
    }

    const getDateOfBirthErrMessage = () => {
        if (!dateOfBirthTouched && !submit1Attempted) {
            return null;
        }
        if (dateOfBirthErrState === 'empty') {
            return translate('enter_your_dob');
        }
        if (dateOfBirthErrState === 'invalid') {
            return translate('check_dob_try_again');
        }
        return null;
    };

    const onDateOfBirthChage = (dob: DateObject) => {
        formStart();
        setDateOfBirth(dob);
    };

    const onLastNameChange = (name: string) => {
        formStart();
        name = name.trim();
        setLastName(name);
    };

    const onSuffixChange = (suffixVal: string) => {
        formStart();
        setSuffix(suffixVal);
    };

    const goToLogin = () => history.push('/login');

    const next = () => {
        setStep(i => i + 1);
    };

    const enterInformationSubmit = async () => {
        setSubmit1Attempted(true);
        if (enterInfoLoading) {
            return;
        }
        conversion.attempt();
        const newInvalidFields: InvalidField<ForgotPassword1Fields>[] = [];
        if (usernameErrState) {
            newInvalidFields.push({ name: 'username', type: usernameErrState });
        }
        if (mbiErrState) {
            newInvalidFields.push({ name: 'mbi', type: mbiErrState });
        }
        if (lastNameErrState) {
            newInvalidFields.push({ name: 'lastName', type: lastNameErrState });
        }
        if (dateOfBirthErrState) {
            newInvalidFields.push({ name: 'dateOfBirth', type: dateOfBirthErrState });
        }
        setStep1InvalidFields(newInvalidFields);
        if (newInvalidFields.length) {
            return;
        }

        setEnterInfoLoading(true);
        try {
            setStep1ApiErrResponse(null);
            const question = await forgotPasswordApi.getSecretQuestion({
                mbi: medicareNumber,
                username,
                lastName,
                suffix,
                dateOfBirth
            });
            setSecretQuestion(question);
            next();
        } catch (e: any) {
            setStep1ApiErrResponse(e);
        } finally {
            setEnterInfoLoading(false);
        }
    };

    const title = enterInformationAlert ? translate("make_sure_its_you") : undefined;
    const showAlert = !step1ApiErrResponse && enterInformationAlert;

    return (
        <Step 
            stepNum={1} 
            stepTitle={translate('enter_your_information')} 
            title={title} 
            showAlert={showAlert} 
            allFieldsRequired
        >
            <SmartErrorAlert
                name="ForgotPassword1"
                usernameRef={usernameRef}
                mbiRef={medicareNumRef}
                lastNameRef={lastNameRef}
                dateOfBirthRef={monthFieldRef}
                conversion={conversion}
                apiResponse={step1ApiErrResponse}
                invalidFields={step1InvalidFields}
                onInvalidField={onStep1InvalidFields}
                parentHeading={infoHeader}
            />
            <TextField
                errorMessage={getUsernameErrMessage()}
                inputRef={setUsernameRef}
                label={translate('username')}
                name={'username'}
                onBlur={() => setUsernameTouched(true)}
                onChange={onUsernameChange}
                onClick={formStart}
                value={username}
            />
            <MedicareNumberSection
                errorMessage={getMbiErrMessage()}
                inputRef={setMedicareNumRef}
                onBlur={() => setMbiTouched(true)}
                onChange={onMedicareNumberChange}
                value={medicareNumber}
                parentComponentHeading={infoHeader}
            />
            <LastNameSection
                errorMessage={getLastNameErrMessage()}
                inputRef={setLastNameRef}
                lastNameVal={lastName}
                onLastNameBlur={() => setLastNameTouched(true)}
                onLastNameChange={onLastNameChange}
                onSuffixChange={onSuffixChange}
                suffixVal={suffix}
            />
            <DateFields2
                date={dateOfBirth}
                hint={translate('use_format_mmddyyyy')}
                label={translate('date_of_birth')}
                onChange={onDateOfBirthChage}
                onComponentBlur={() => setDateOfBirthTouched(true)}
                monthFieldRef={setMonthFieldRef}
                errorMessage={getDateOfBirthErrMessage()}
            />
            <Buttons
                backText={translate('cancel')}
                forwardText={translate('next')}
                isLoading={enterInfoLoading}
                onBackClick={goToLogin}
                onForwardClick={enterInformationSubmit}
            />
        </Step>
    );
}